import React, { useState } from 'react';
import { Card, Button, Flex, Typography, theme, Modal, Dropdown, notification, Tag, Tabs, Divider } from 'antd';
import { EditOutlined, DeleteOutlined, CopyOutlined, MoreOutlined, NodeIndexOutlined, PlayCircleOutlined, SettingOutlined } from '@ant-design/icons';
import { ProcessFlow } from '../../../../types';
import { ProcessFlowSelector } from '../ProcessFlowSelector';
import { ProcessFlowParameter } from '../ProcessFlowParametersConfig';

import { createLogger } from '../../../../../../utils/logger';
const logger = createLogger('features-assistants');

const { useToken } = theme;
const { Text } = Typography;

interface CardProcessFlowActionProps {
  message: string;
  onDelete: () => void;
  onDuplicate: () => void;
  onEdit: (newMessage: string) => void;
  assistantId: string;
  flowId?: string;
}

export const CardProcessFlowAction: React.FC<CardProcessFlowActionProps> = ({
  message,
  onDelete,
  onDuplicate,
  onEdit,
  assistantId,
  flowId
}) => {
  const { token } = useToken();
  const [showEditModal, setShowEditModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedProcessFlow, setSelectedProcessFlow] = useState<ProcessFlow | null>(null);

  // Extraer los datos del process flow del mensaje
  const extractProcessFlowData = (msg: string) => {
    try {
      const match = msg.match(/\{.*\}/);
      if (match) {
        const processFlowData = JSON.parse(match[0]);
        return {
          type: processFlowData.type || 'processflow',
          processFlowId: processFlowData.processFlowId || '',
          processFlowName: processFlowData.processFlowName || '',
          description: processFlowData.description || '',
          status: processFlowData.status || 'active',
          parameters: processFlowData.parameters || [],
          timestamp: processFlowData.timestamp || new Date().toISOString()
        };
      }
      return {
        type: 'processflow',
        processFlowId: '',
        processFlowName: '',
        description: '',
        status: 'active',
        parameters: [],
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      logger.error('Error parsing process flow message:', error);
      return {
        type: 'processflow',
        processFlowId: '',
        processFlowName: '',
        description: '',
        status: 'active',
        parameters: [],
        timestamp: new Date().toISOString()
      };
    }
  };

  const processFlowData = extractProcessFlowData(message);

  const handleEditClick = () => {
    setShowEditModal(true);
  };

  const handleProcessFlowSelect = (processFlow: ProcessFlow | null) => {
    setSelectedProcessFlow(processFlow);
  };

  const handleSaveProcessFlow = (messageString: string) => {
    onEdit(messageString);
    setShowEditModal(false);

    notification.success({
      message: 'Success',
      description: 'Process Flow configuration saved'
    });
  };

  const handleFormCancel = () => {
    setShowEditModal(false);
    setSelectedProcessFlow(null);
  };

  const handleDeleteClick = async () => {
    setIsDeleting(true);
    try {
      onDelete();
      notification.success({
        message: 'Success',
        description: 'Process Flow removed from flow'
      });
    } catch (error: any) {
      logger.error('Error in delete process:', error);
      notification.error({
        message: 'Error',
        description: error?.message || 'Failed to remove Process Flow'
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const menuItems = [
    {
      key: 'edit',
      label: 'Edit',
      icon: <EditOutlined />,
      onClick: handleEditClick
    },
    {
      key: 'duplicate',
      label: 'Duplicate',
      icon: <CopyOutlined />,
      onClick: onDuplicate
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: <DeleteOutlined />,
      onClick: handleDeleteClick,
      disabled: isDeleting
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'green';
      case 'inactive':
        return 'red';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active':
        return 'Active';
      case 'inactive':
        return 'Inactive';
      default:
        return status;
    }
  };

  return (
    <>
      <Card
        style={{
          maxWidth: '400px',
          width: '100%',
          borderRadius: token.borderRadiusLG,
          boxShadow: token.boxShadowTertiary,
          cursor: 'pointer'
        }}
        bodyStyle={{ padding: token.paddingSM }}
        onClick={handleEditClick}
      >
        <Flex align="center" justify="space-between" style={{ width: '100%' }}>
          <Flex gap={token.marginMD} align="center">
            <div
              style={{
                width: 120,
                height: 70,
                backgroundColor: '#722ed1',
                borderRadius: token.borderRadiusLG,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '12px',
                fontWeight: 'bold',
                textAlign: 'center',
                padding: '8px'
              }}
            >
              <NodeIndexOutlined style={{ fontSize: '24px', marginRight: '8px' }} />
              Process Flow
            </div>
            <Flex vertical gap={token.marginXS}>
              <Flex align="center" gap={token.marginXS}>
                <Text strong style={{ fontSize: '16px', lineHeight: '24px', margin: 0 }}>
                  {processFlowData.processFlowName || 'Process Flow Execution'}
                </Text>
                <Tag color={getStatusColor(processFlowData.status)}>
                  {getStatusText(processFlowData.status)}
                </Tag>
              </Flex>
              <Text type="secondary" style={{ fontSize: '14px', lineHeight: '20px', margin: 0 }}>
                {processFlowData.description || 'Execute automated workflow'}
              </Text>
              {processFlowData.parameters && processFlowData.parameters.length > 0 && (
                <Flex align="center" gap="4px" style={{ marginTop: '4px' }}>
                  <SettingOutlined style={{ fontSize: '12px', color: token.colorTextSecondary }} />
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {processFlowData.parameters.length} parameter{processFlowData.parameters.length !== 1 ? 's' : ''} configured
                  </Text>
                </Flex>
              )}
            </Flex>
          </Flex>
          <div onClick={(e) => e.stopPropagation()}>
            <Dropdown
              menu={{ items: menuItems }}
              trigger={['click']}
              placement="bottomRight"
            >
              <Button
                icon={<MoreOutlined />}
                size="small"
                type="text"
                loading={isDeleting}
              />
            </Dropdown>
          </div>
        </Flex>
      </Card>

      <Modal
        open={showEditModal}
        onCancel={handleFormCancel}
        footer={null}
        width={700}
        destroyOnClose={true}
      >
        <ProcessFlowSelector
          assistantId={assistantId}
          onSave={handleSaveProcessFlow}
          onCancel={handleFormCancel}
          compact={true}
          initialData={{
            selectedWorkflowId: processFlowData.processFlowId,
            parameters: processFlowData.parameters
          }}
        />
      </Modal>
    </>
  );
};